import { useCallback, useEffect, useState, useSyncExternalStore, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Plus, Settings } from '../../../icons';
import { Button, EmptyState, PageHeader, SearchInput } from '../../../components/shared';
import {
  getBoard, getBoardPost, getBoardSummary, getConfirmsMine,
  type BoardConfirmMineItem, type BoardConfirmsMineDto, type BoardPrefixDto, type BoardSummaryDto,
} from '../../../api/boards';
import { getBoardsLang, subscribeBoardsLang, tBoards } from '../../../lib/boards-i18n';
import { markBoardTrashUnsupported } from '../board-trash-support';
import { useBoards } from '../boards-context';
import {
  boardListPath, boardPostPath, boardSettingsPath, boardWritePath,
  buildBoardListSearch, parseBoardListQuery, type BoardListQuery,
} from '../board-v1-url';
import { countManagers } from './board-list-model';
import { BoardKpiRow } from './BoardKpiRow';
import { BoardPostListCard } from './BoardPostListCard';
import { BoardTrashCard } from './BoardTrashCard';
import './board-list.css';

/**
 * 게시판 목록 화면 — 시안 v1 `axi-board-list` (U2 · 2026-09-15).
 *
 *   머리(경로 · 제목 · 모든 게시판 찾기 · 글쓰기)
 *   숫자 칸 넷(결정 Q3 — 「안 읽은 글」만 웹 계산, 셋은 「아직 없음」)
 *   [트리 카드 272 | 목록 카드]  ← 결정 Q1
 *
 * 목록 카드는 세 모습이다: 글 목록(`/board/:id`) · 휴지통(`/board/:id/trash`) · 볼 수 없음.
 *
 * ★★ **볼 수 없음은 한 문구다**(계획 규칙 15) — 서버가 볼 수 없는 게시판에 404 를 주므로
 *    「없어졌다」와 「권한이 없다」를 가를 수 없다. 「운영자에게 요청」 단추는 **없다**(관제 Q-G ·
 *    서버 R10 전 · 누를 수 없는 단추를 두지 않는다). 정본 G1 도 권한 없음에는 행동을 두지 않는다.
 * ★ 판정: `GET /boards` 에 그 id 가 없거나, 상세 · 글 목록이 404 를 주면.
 * ★ 트리 · 숫자 칸을 다시 셀 때(모두 읽음 · 복원 뒤) **화면을 비우지 않는다** — 목록을 새로 읽는 동안
 *   카드가 사라졌다 나타나면 읽던 자리를 잃는다.
 */

type ApiError = { response?: { status?: number; data?: { message?: string } }; message?: string };

export interface BoardListScreenProps {
  boardId: string;
  mode: 'list' | 'trash';
}

export function BoardListScreen({ boardId, mode }: BoardListScreenProps) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const query = parseBoardListQuery(sp);

  /** ★ 목록은 셸이 읽는다 — 여기서 또 읽으면 같은 요청이 두 번 나가고 트리·본문이 어긋난다. */
  const { state: boardsState, recount, version: boardsKey } = useBoards();
  const [detail, setDetail] = useState<{ boardId: string; prefixes: BoardPrefixDto[]; managers: number | null } | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [headDraft, setHeadDraft] = useState(query.scope === 'all' ? query.q : '');
  /**
   * 게시판 요약(R7) — **주별 새 글** 칸에만 쓴다.
   * ★ `getBoardSummary` 는 실패해도 던지지 않고 `null` 을 준다 — 옛 서버(404)나 네트워크가
   *   흔들려도 목록은 그대로 뜨고, 그 칸만 종전처럼 「아직 없음」이 된다.
   */
  const [summary, setSummary] = useState<BoardSummaryDto | null>(null);
  /**
   * 「내 차례 확인 공지」(R8) — 넷째 칸의 목록이자 셋째 칸(도넛)이 볼 글을 고르는 곳.
   * 요약과 같은 판단으로 **따로** 읽는다: 늦거나 없어도(옛 서버 404) 목록을 막지 않는다.
   */
  const [confirms, setConfirms] = useState<BoardConfirmsMineDto | null>(null);
  /**
   * 도넛이 쓸 «확인 수가 실린 글». 목록 응답에는 분모(`targetCount`)가 없어서
   * (읽을 수 있는 사람을 따져야 나오는 값이라 서버가 목록엔 안 싣는다) **상세를 한 번 더** 읽는다.
   * 확인 요청 공지가 하나도 없으면 아예 안 읽는다.
   */
  const [ringSource, setRingSource] = useState<
    { title: string; targetCount?: number; readCount?: number; confirmCount?: number } | null
  >(null);

  // 요약은 목록과 **따로** 읽는다 — 늦거나 없어도 목록을 막지 않는다.
  useEffect(() => {
    let cancelled = false;
    void getBoardSummary().then((s) => { if (!cancelled) setSummary(s); });
    return () => { cancelled = true; };
  }, [boardsKey]);

  // 확인 공지도 따로. 첫 글이 있으면 그 상세를 한 번 더 읽어 도넛의 분모를 얻는다.
  useEffect(() => {
    let cancelled = false;
    void getConfirmsMine().then(async (c) => {
      if (cancelled) return;
      setConfirms(c);
      const head = c?.items[0];
      if (!head) { setRingSource(null); return; }
      try {
        const d = await getBoardPost(head.postId);
        if (cancelled) return;
        setRingSource({
          title: d.post.title,
          targetCount: d.post.targetCount,
          readCount: d.post.readCount,
          confirmCount: d.post.confirmCount,
        });
      } catch {
        // 그 글을 못 읽어도 목록·배지는 그대로다 — 도넛만 빈 채로 선다.
        if (!cancelled) setRingSource(null);
      }
    });
    return () => { cancelled = true; };
  }, [boardsKey]);

  const openConfirmPost = useCallback((it: BoardConfirmMineItem) => {
    navigate(boardPostPath(it.postId));
  }, [navigate]);

  const boards = boardsState.status === 'ready' ? boardsState.boards : [];
  const active = boards.find((b) => b.id === boardId) ?? null;
  const activeId = active?.id ?? null;

  useEffect(() => { setForbidden(false); }, [boardId]);
  useEffect(() => { setHeadDraft(query.scope === 'all' ? query.q : ''); }, [query.q, query.scope]);

  useEffect(() => {
    if (!activeId) { setDetail(null); return undefined; }
    let cancelled = false;
    getBoard(activeId)
      .then((d) => { if (!cancelled) setDetail({ boardId: activeId, prefixes: d.prefixes, managers: countManagers(d.members) }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        if ((e as ApiError)?.response?.status === 404) { setForbidden(true); return; }
        // 말머리 · 운영자 수를 못 받아도 목록은 선다 — 칩이 비고 운영자 수를 안 적을 뿐이다.
        setDetail({ boardId: activeId, prefixes: [], managers: null });
      });
    return () => { cancelled = true; };
  }, [activeId]);

  const setQuery = useCallback((next: Partial<BoardListQuery>, opts?: { replace?: boolean }) => {
    const merged = { ...parseBoardListQuery(sp), ...next };
    setSp(new URLSearchParams(buildBoardListSearch(merged).replace(/^\?/, '')), { replace: opts?.replace });
  }, [sp, setSp]);


  const ready = boardsState.status === 'ready';
  const denied = ready && (!active || forbidden);
  const canWrite = !!active && !denied && active.canWrite !== false;
  const crumb = tBoards('boards.v1.crumb');

  const submitHeadSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = headDraft.trim();
    if (!q) return;
    navigate(boardListPath(activeId ?? boardId, { q, scope: 'all' }));
  };

  const cardMeta = active
    ? [
      tBoards(active.isPrivate ? 'boards.v1.card.private' : 'boards.v1.card.shared'),
      detail?.boardId === active.id && detail.managers != null ? tBoards('boards.v1.card.managers', { n: detail.managers }) : null,
    ].filter(Boolean).join(' · ')
    : '';

  return (
    <div
      className="bv1-page"
      data-board-screen={mode}
      data-id={boardId}
      data-tab={mode === 'list' ? query.sort : undefined}
    >
      <PageHeader
        className="bv1-head"
        breadcrumb={[crumb, active?.name ?? crumb]}
        title={crumb}
        actions={(
          <>
            <form role="search" className="bv1-head__search" onSubmit={submitHeadSearch}>
              <SearchInput
                value={headDraft}
                placeholder={tBoards('boards.v1.head.search_all')}
                aria-label={tBoards('boards.v1.head.search_all')}
                onChange={(e) => setHeadDraft(e.target.value)}
                onClear={() => setHeadDraft('')}
              />
            </form>
            <Button
              variant="primary"
              iconLeft={<Plus aria-hidden />}
              disabled={!canWrite}
              {...(active && !canWrite && !denied ? { title: tBoards('boards.wide.write_forbidden') } : {})}
              onClick={() => { if (active) navigate(boardWritePath(active.id)); }}
            >
              {tBoards('boards.btn.write')}
            </Button>
          </>
        )}
      />

      {boardsState.status === 'error' ? (
        <EmptyState
          kind="error"
          title={tBoards('boards.v1.load_failed')}
          description={boardsState.message}
          action={<Button variant="secondary" size="compact" onClick={recount}>{tBoards('boards.v1.retry')}</Button>}
        />
      ) : (
        <>
          <BoardKpiRow
            boards={boards}
            summary={summary}
            confirms={confirms}
            confirmRingSource={ringSource}
            onOpenPost={openConfirmPost}
          />

          {/* ★ 2026-09-17 사용자 지시 — 게시판 트리는 여기(본문 격자) 가 아니라 **셸 좌측 메뉴**에 산다
              (`../BoardShell.tsx`). 그래야 글 상세·글쓰기·설정에서도 좌측이 그대로 선다.
              `.bv1-layout` 은 한 칸 격자로 남겨 둔다 — 본문 카드의 자리·여백이 종전과 같도록. */}
          <div className="bv1-layout">
            <section className="bv1-card bv1-main" aria-busy={!ready || undefined}>
              {!ready ? (
                <div className="bv1-loading">{tBoards('boards.v1.loading')}</div>
              ) : denied ? (
                <>
                  <div className="bv1-card__head">
                    <h2 className="bv1-card__title">{crumb}</h2>
                  </div>
                  <EmptyState
                    kind="no-permission"
                    icon={Lock}
                    title={tBoards('boards.v1.deny.title')}
                    description={tBoards('boards.v1.deny.desc')}
                  />
                </>
              ) : active && mode === 'trash' ? (
                <>
                  <div className="bv1-card__head">
                    <h2 className="bv1-card__title">{tBoards('boards.trash.title')}</h2>
                    <span className="bv1-card__sub">{tBoards('boards.v1.trash.sub', { name: active.name })}</span>
                  </div>
                  <BoardTrashCard
                    board={active}
                    onRestored={recount}
                    onUnsupported={markBoardTrashUnsupported}
                  />
                </>
              ) : active ? (
                <>
                  <div className="bv1-card__head">
                    <h2 className="bv1-card__title">{active.name}</h2>
                    {cardMeta && <span className="bv1-card__sub">{cardMeta}</span>}
                    {active.canManage && (
                      <span className="bv1-card__end">
                        <Button
                          variant="secondary"
                          size="compact"
                          iconLeft={<Settings aria-hidden />}
                          aria-label={tBoards('boards.aria.settings', { name: active.name })}
                          onClick={() => navigate(boardSettingsPath(active.id))}
                        >
                          {tBoards('boards.btn.settings')}
                        </Button>
                      </span>
                    )}
                  </div>
                  {active.description && <p className="bv1-card__desc">{active.description}</p>}
                  <BoardPostListCard
                    key={active.id}
                    board={active}
                    prefixes={detail?.boardId === active.id ? detail.prefixes : []}
                    query={query}
                    onQueryChange={setQuery}
                    onOpenPost={(postId, state) => navigate(boardPostPath(postId), { state })}
                    onWrite={() => navigate(boardWritePath(active.id))}
                    onForbidden={() => setForbidden(true)}
                    onReadChanged={recount}
                  />
                </>
              ) : null}
            </section>
          </div>
        </>
      )}

    </div>
  );
}
